import React, { useState, useEffect } from 'react';
import './PromptDisplayPanel.css';

const API_BASE_URL = (import.meta as any).env?.VITE_API_URL ?? '';

interface QCStandardDetail {
  id: number;
  name: string;
  source_file: string | null;
  sheet_name: string | null;
  description: string | null;
  evaluation_prompt: string;
  dimensions: any[] | null;
  is_active: boolean;
  created_at: string | null;
}

interface PromptDisplayPanelProps {
  standardId: number | null;
}

// 默认ACE标准的prompt
const DEFAULT_ACE_PROMPT = `你是一位专业的客服质检专家，基于ACE CX QA Culture Rubric评价体系对客服通话进行评估。

## 评价维度

### 1. Accurate（准确性）
- 信息准确性：提供的信息是否正确、完整
- 流程合规性：是否按照标准流程执行
- 问题解决：是否准确理解并解决客户问题

### 2. Customized（个性化）
- 服务个性化：是否根据客户特点提供定制化服务
- 客户关怀：是否展现同理心和关怀
- 沟通技巧：是否运用有效的沟通技巧

### 3. Efficient（高效性）
- 响应效率：是否及时响应客户需求
- 问题处理效率：是否高效解决问题
- 时间管理：是否合理控制通话时长

## ZTP零容忍政策
- 严禁使用侮辱性语言
- 严禁歧视客户
- 严禁泄露客户隐私
- 严禁推诿责任

请按照以上标准对客服通话进行全面评估，给出1-5分的评分和详细分析。`;

export const PromptDisplayPanel: React.FC<PromptDisplayPanelProps> = ({ standardId }) => {
  const [standard, setStandard] = useState<QCStandardDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (standardId) {
      fetchStandardDetail(standardId);
    } else {
      setStandard(null);
      setError(null);
    }
  }, [standardId]);

  const fetchStandardDetail = async (id: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/qc-standards/${id}`);
      if (!response.ok) throw new Error('获取标准详情失败');
      const data = await response.json();
      setStandard(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : '未知错误');
    } finally {
      setLoading(false);
    }
  };

  const displayPrompt = standard?.evaluation_prompt || DEFAULT_ACE_PROMPT;
  const standardName = standard?.name || 'ACE CX QA (默认)';
  const isDefault = !standardId;

  return (
    <div className="prompt-panel">
      <div
        className="prompt-header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="prompt-header-left">
          <span className="prompt-icon">📋</span>
          <span className="prompt-title">评估标准 Prompt</span>
          {standard && (
            <span className="prompt-standard-name">{standard.name}</span>
          )}
        </div>
        <div className="prompt-header-right">
          {isDefault && (
            <span className="prompt-badge default">默认</span>
          )}
          <span className={`prompt-arrow ${isExpanded ? 'expanded' : ''}`}>
            ▼
          </span>
        </div>
      </div>

      {isExpanded && (
        <div className="prompt-body">
          {loading && (
            <div className="prompt-loading">加载中...</div>
          )}

          {error && (
            <div className="prompt-error">{error}</div>
          )}

          {!loading && !error && (
            <>
              <div className="prompt-meta">
                <span className="prompt-meta-item">
                  <strong>标准名称:</strong> {standardName}
                </span>
                {standard?.source_file && (
                  <span className="prompt-meta-item">
                    <strong>来源:</strong> {standard.source_file}
                    {standard.sheet_name && ` / ${standard.sheet_name}`}
                  </span>
                )}
                {standard?.dimensions && (
                  <span className="prompt-meta-item">
                    <strong>评估维度:</strong> {standard.dimensions.length} 个
                  </span>
                )}
              </div>

              <div className="prompt-content">
                <pre>{displayPrompt}</pre>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
